import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableContainer,
  Paper,
  makeStyles,
  TextField,
  Chip,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { ViewWinnerContext } from "../../../contexts/ViewWinnerContext";
import { getLang } from "app/feature/constants";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 800,
    maxWidth: 1000,
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #E0E0E0",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
  },
  tableContainer: {
    maxHeight: 400,
    overflow: "auto",
    marginBottom: theme.spacing(2),
    border: "1px solid #E0E0E0",
  },
  tableHeaderCell: {
    backgroundColor: "#F5F5F5",
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottom: "2px solid #E0E0E0",
    padding: theme.spacing(1.5),
  },
  tableBodyCell: {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid #F0F0F0",
  },
  claimStatusChip: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    fontSize: "0.75rem",
    fontWeight: 600,
    display: "inline-block",
  },
  statusChips: {
    display: "flex",
    gap: 8,
    marginTop: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #E0E0E0",
  },
  saveButton: {
    backgroundColor: "#3A4D54",
    color: "#ffffff",
    textTransform: "uppercase",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    "&:hover": {
      backgroundColor: "#2C3A3F",
    },
  },
  cancelButton: {
    textTransform: "uppercase",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
  },
}));

export default function BulkProcessDialogComponent() {
  const classes = useStyles();
  const {
    lang,
    bulkProcessDialog,
    handleCloseBulkProcessDialog,
    handleConfirmBulkProcess,
    isUpdating,
  } = useContext(ViewWinnerContext);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [adminNotes, setAdminNotes] = React.useState("");
  const [claimStatus, setClaimStatus] = React.useState(1); // Default to 'Processed' (1)

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!bulkProcessDialog.winners || bulkProcessDialog.winners.length === 0) {
    return null;
  }

  const winners = bulkProcessDialog.winners;
  const displayedWinners = winners.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Helper function to get form value by field name
  const getFormValue = (winner, fieldName) => {
    const formValue = winner.form_values?.find(
      (fv) => fv.field_name === fieldName
    );
    return formValue?.value || null;
  };

  // Get winner name from form_values
  const getWinnerName = (winner) => {
    const nameValue = getFormValue(winner, "name");
    return nameValue || winner.participant?.name || "-";
  };

  // Get winner contact from form_values - use raw unmasked data for bulk process
  const getWinnerContact = (winner) => {
    const contactValue = getFormValue(winner, "contactNumber");
    if (contactValue && typeof contactValue === "object") {
      return `${contactValue.contactCode || "+60"}${
        contactValue.contactNumber || ""
      }`;
    }
    // Use rawContact (unmasked) if available, otherwise fall back to participant data
    return winner.rawContact || winner.participant?.contact_no || "-";
  };

  // Get winner email from form_values - use raw data
  const getWinnerEmail = (winner) => {
    const emailValue = getFormValue(winner, "email");
    // Use rawEmail if available
    return emailValue || winner.rawEmail || winner.participant?.email || "-";
  };

  // Get winner address from form_values - use raw data
  const getWinnerAddress = (winner) => {
    const addressValue = getFormValue(winner, "address");
    // Use rawAddress if available
    return (
      addressValue || winner.rawAddress || winner.participant?.address || "-"
    );
  };

  return (
    <Dialog
      open={bulkProcessDialog.open}
      onClose={handleCloseBulkProcessDialog}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography
          variant="h6"
          style={{ fontWeight: "bold", fontSize: "1.125rem" }}
        >
          {getLang(lang, "label.BULK_PROCESS")}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleCloseBulkProcessDialog}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>
                  {getLang(lang, "label.PRIZE_NAME")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {getLang(lang, "label.WINNER_NAME")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {getLang(lang, "label.WINNER_PHONE_NO")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {getLang(lang, "label.WINNER_EMAIL")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {getLang(lang, "label.WINNER_ADDRESS")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedWinners.map((winner, index) => (
                <TableRow key={winner.uuid || index}>
                  <TableCell className={classes.tableBodyCell}>
                    {winner.prizeName || "-"}
                  </TableCell>
                  <TableCell className={classes.tableBodyCell}>
                    {getWinnerName(winner)}
                  </TableCell>
                  <TableCell className={classes.tableBodyCell}>
                    {getWinnerContact(winner)}
                  </TableCell>
                  <TableCell className={classes.tableBodyCell}>
                    {getWinnerEmail(winner)}
                  </TableCell>
                  <TableCell className={classes.tableBodyCell}>
                    {getWinnerAddress(winner)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={winners.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage={getLang(lang, "label.ROWS_PER_PAGE")}
        />

        <Box mt={2}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {getLang(lang, "label.CLAIM_STATUS")}
          </Typography>
          <Box className={classes.statusChips}>
            <Chip
              label={getLang(lang, "label.UNPROCESSED")}
              clickable
              onClick={() => setClaimStatus(0)}
              style={{
                backgroundColor: claimStatus === 0 ? "#FF9800" : "transparent",
                color: claimStatus === 0 ? "#ffffff" : "#FF9800",
                fontWeight: 600,
                cursor: "pointer",
                border: claimStatus === 0 ? "none" : "1px solid #FF9800",
              }}
            />
            <Chip
              label={getLang(lang, "label.PROCESSED")}
              clickable
              onClick={() => setClaimStatus(1)}
              style={{
                backgroundColor: claimStatus === 1 ? "#6AAF68" : "transparent",
                color: claimStatus === 1 ? "#ffffff" : "#6AAF68",
                fontWeight: 600,
                cursor: "pointer",
                border: claimStatus === 1 ? "none" : "1px solid #6AAF68",
              }}
            />
            <Chip
              label={getLang(lang, "label.REJECTED")}
              clickable
              onClick={() => setClaimStatus(2)}
              style={{
                backgroundColor: claimStatus === 2 ? "#D32F2F" : "transparent",
                color: claimStatus === 2 ? "#ffffff" : "#D32F2F",
                fontWeight: 600,
                cursor: "pointer",
                border: claimStatus === 2 ? "none" : "1px solid #D32F2F",
              }}
            />
          </Box>
        </Box>

        <Box mt={2}>
          <Typography
            variant="body2"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            {getLang(lang, "label.ADMIN_NOTES")}
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder={getLang(lang, "placeholder.YOUR_NOTES_HERE")}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            style={{
              backgroundColor: "#F5F5F5",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCloseBulkProcessDialog}
          disabled={isUpdating}
          className={classes.cancelButton}
        >
          {getLang(lang, "label.CANCEL")}
        </Button>
        <Button
          onClick={() => handleConfirmBulkProcess(claimStatus, adminNotes)}
          variant="contained"
          disabled={isUpdating}
          className={classes.saveButton}
        >
          {getLang(lang, "label.SAVE")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
